import React, { useState } from 'react';
import { Drawer, Button } from '@/components/ui';
import { Send, User, Bot, RefreshCw, Sparkles, Trash2, BookOpen } from 'lucide-react';
import { useAI } from './AIContext';
import { AIGuideModal } from './AIGuideModal';
import { useAuth } from '../auth/AuthContext';
import { cn } from '@/utils/cn';
import { MarkdownText } from '@/components/ui/MarkdownText';
import { ChatInput } from '@/components/ui/ChatInput';

export function GlobalAIChatDrawer() {
  const { isOpen, closeChat, messages, sendMessage, isAnalyzing, clearMessages, usedLimit, maxLimit } = useAI();
  const { profile } = useAuth();
  const [input, setInput] = useState('');
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const gender = profile?.gender?.toLowerCase();
  const honorific = gender === 'male' ? 'Anh' : gender === 'female' ? 'Chị' : 'Anh/Chị';

  const placeholders = [
    `${honorific} cần em hỗ trợ gì cho dự án hôm nay ạ?`,
    `Hôm nay mình triển khai hạng mục nào vậy ${honorific}?`,
    `${honorific} cứ nhập câu hỏi, em sẽ giải đáp ngay nhé...`,
    `${honorific} muốn em kiểm tra dữ liệu hay tư vấn gì không?`,
    `Em đang đợi tin nhắn của ${honorific} đây ạ...`,
    `Có vấn đề gì cần em cùng giải quyết không ${honorific}?`
  ];

  const [activePlaceholder] = useState(() => placeholders[Math.floor(Math.random() * placeholders.length)]);

  const handleSend = (imageData?: string) => {
    if ((!input.trim() && !imageData) || isAnalyzing) return;
    sendMessage(input, imageData);
    setInput('');
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={closeChat}
      title={
        <div className="flex items-center justify-between w-full pr-10">
          <span>Trợ lý Landscape</span>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsGuideOpen(true)}
              className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-lg transition-all flex items-center gap-2 text-[11px] font-bold group border border-emerald-100 shadow-sm"
              title="Sổ tay Hướng dẫn Hỏi AI"
            >
              <BookOpen size={14} />
              <span>Sổ tay</span>
            </button>
            {messages.length > 1 && (
              <button 
                onClick={clearMessages}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-rose-600 rounded-lg transition-all flex items-center gap-2 text-[11px] font-bold group"
                title="Xóa lịch sử hội thoại"
              >
                <RefreshCw size={14} className="group-hover:animate-spin" />
                <span>Làm mới</span>
              </button>
            )}
          </div>
        </div>
      }
      size="md"
      footer={
        <div className="w-full bg-white p-4 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="sm:text-sm text-xs font-medium text-slate-500 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              Lượt hỏi AI hôm nay:
            </span>
            <span className={cn(
              "sm:text-sm text-xs font-bold px-2.5 py-0.5 rounded-full",
              usedLimit >= maxLimit ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
            )}>
              {usedLimit} / {maxLimit}
            </span>
          </div>
          <ChatInput 
            value={input}
            onChange={setInput}
            onSend={handleSend}
            placeholder={usedLimit >= maxLimit ? "Sếp đã dùng hết lượt hỏi hôm nay rồi ạ..." : activePlaceholder}
            disabled={isAnalyzing || usedLimit >= maxLimit}
          />
          <div className="mt-3 mx-auto max-w-fit flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 py-1.5 px-3 rounded-lg border border-emerald-200/60 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span>Sử dụng AI đúng hướng dẫn sẽ giúp tiết kiệm thời gian, chi phí và tránh các thao tác dư thừa</span>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 p-1 custom-scrollbar min-h-[400px]">
          {messages.map((msg, idx) => (
            <div key={idx} className={cn("flex gap-3", msg.role === 'user' ? "flex-row-reverse" : "flex-row")}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center shrink-0 border shadow-sm overflow-hidden",
                msg.role === 'user' ? "bg-white text-emerald-700 border-emerald-100" : "bg-gradient-to-br from-emerald-600 to-teal-700 text-white"
              )}>
                {msg.role === 'user' ? (
                  profile?.avatar_path ? <img src={profile.avatar_path} alt="User" className="w-full h-full object-cover" /> : <User size={16} />
                ) : <Bot size={16} />}
              </div>
              <div className={cn(
                "p-4 rounded-xl shadow-sm max-w-[85%] whitespace-pre-wrap leading-relaxed",
                msg.role === 'user' ? "bg-emerald-600 text-white rounded-tr-none text-sm" : "bg-white border border-emerald-50 text-slate-800 rounded-tl-none shadow-emerald-50/50"
              )}>
                {msg.data?.image && msg.role === 'user' && (
                  <div className="mb-2.5">
                    <img src={msg.data.image} alt="User upload" className="max-w-full h-auto max-h-48 rounded-xl border-2 border-emerald-100/50 shadow-sm object-contain bg-white" />
                  </div>
                )}
                {msg.data?.agent && msg.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-slate-100 text-[10px] font-bold text-emerald-600 uppercase tracking-tight">
                    <i className={cn(msg.data.agent.icon, "text-xs")} />
                    {msg.data.agent.name}
                  </div>
                )}
                {msg.role === 'assistant' ? (
                  <MarkdownText content={msg.content} />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}

          {isAnalyzing && (
            <div className="flex gap-3 items-center text-slate-400 text-xs italic">
              <RefreshCw className="w-3 h-3 animate-spin text-emerald-500" />
              Đang kết nối dữ liệu ...
            </div>
          )}
        </div>
      </div>
      
      <AIGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
    </Drawer>
  );
}
